/**
 * Skills Import Page (admin only) — /skills/import
 *
 * Dedicated page for importing weekly TRAINING PLANS from CSV (one level per
 * import). Documents the required format and the import/overwrite behavior,
 * and requires a preview (dry-run) + explicit confirm before writing.
 *
 * Flow: input -> preview -> done. See
 * aidlc-docs/construction/skills-import-page/functional-design/frontend-components.md
 */

import { useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { restClient } from '../api/rest-client';
import { useUIStore } from '../store/uiStore';
import { Button } from '../components/common/Button';
import {
  TRAINING_PLAN_LEVELS,
  type TrainingPlanLevel,
  type TrainingPlanImportPreview,
  type TrainingPlanImportResult,
} from '../types/trainingPlans';
import {
  TRAINING_PLAN_CSV_HEADERS,
  buildTrainingPlanCsvTemplate,
  downloadCsv,
} from '../utils/trainingPlanCsv';

const INPUT_CLASSES =
  'w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500';

type Phase = 'input' | 'preview' | 'done';

interface ApiError {
  message: string;
  details?: string[];
}

/** Narrow an unknown thrown value into a message + optional validation details. */
function extractApiError(err: unknown): ApiError {
  if (err && typeof err === 'object' && 'response' in err) {
    const resp = (err as { response?: { data?: unknown } }).response;
    const data = resp?.data;
    if (data && typeof data === 'object') {
      const d = data as { error?: unknown; details?: unknown };
      return {
        message: typeof d.error === 'string' ? d.error : 'Request failed',
        details: Array.isArray(d.details)
          ? d.details.filter((x): x is string => typeof x === 'string')
          : undefined,
      };
    }
  }
  return { message: err instanceof Error ? err.message : 'Request failed' };
}

export default function SkillsImport() {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const showToast = useUIStore((s) => s.showToast);

  const [phase, setPhase] = useState<Phase>('input');
  const [level, setLevel] = useState<TrainingPlanLevel | ''>('');
  const [csv, setCsv] = useState('');
  const [fileName, setFileName] = useState('');
  const [preview, setPreview] = useState<TrainingPlanImportPreview | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [result, setResult] = useState<TrainingPlanImportResult | null>(null);
  const [busy, setBusy] = useState(false);

  // Admin-only (BR-15)
  if (!isAdmin) {
    return <Navigate to="/skills" replace />;
  }

  // Changing level or CSV invalidates a stale preview (BR-16)
  const resetToInput = () => {
    setPhase('input');
    setPreview(null);
    setValidationErrors([]);
    setResult(null);
  };

  const handleLevelChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setLevel(e.target.value as TrainingPlanLevel | '');
    resetToInput();
  };

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    resetToInput();
    try {
      setCsv(await file.text());
    } catch {
      showToast('error', 'Could not read the file.');
    }
  };

  const handleCsvChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setCsv(e.target.value);
    resetToInput();
  };

  const handlePreview = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!level) {
      showToast('error', 'Choose a level first.');
      return;
    }
    if (!csv.trim()) {
      showToast('error', 'Choose a CSV file or paste CSV text first.');
      return;
    }
    setBusy(true);
    setValidationErrors([]);
    try {
      const data = await restClient.previewTrainingPlanImport({ level, csv });
      setPreview(data);
      setPhase('preview');
    } catch (err) {
      const parsed = extractApiError(err);
      if (parsed.details && parsed.details.length > 0) {
        setValidationErrors(parsed.details);
        setPhase('preview');
      } else {
        showToast('error', parsed.message);
      }
    } finally {
      setBusy(false);
    }
  };

  const handleConfirm = async () => {
    if (!level) return;
    setBusy(true);
    try {
      const data = await restClient.importTrainingPlans({ level, csv });
      setResult(data);
      setPhase('done');
      showToast(
        'success',
        `Imported ${data.weeks_imported} week${data.weeks_imported !== 1 ? 's' : ''} (${data.sessions_imported} sessions) for ${data.level}`,
      );
    } catch (err) {
      showToast('error', extractApiError(err).message);
    } finally {
      setBusy(false);
    }
  };

  const handleTemplate = () => {
    downloadCsv('training-plan-template.csv', buildTrainingPlanCsvTemplate());
  };

  const startOver = () => {
    setCsv('');
    setFileName('');
    resetToInput();
  };

  const hasBlockingErrors = validationErrors.length > 0;

  return (
    <div className="mx-auto max-w-4xl px-4 py-6" data-testid="skills-import-page">
      <div className="mb-4 flex items-center justify-between gap-2">
        <h1 className="text-2xl font-semibold text-primary-900">Import Training Plan</h1>
        <Link
          to="/skills"
          className="text-sm text-primary-600 hover:text-primary-800 hover:underline"
          data-testid="skills-import-back-link"
        >
          ← Back to Training Plans
        </Link>
      </div>

      {/* Instructions + behavior documentation */}
      <section className="mb-6 rounded-xl border border-gray-200 bg-white p-4 sm:p-5">
        <h2 className="text-lg font-semibold text-primary-900">How importing works</h2>
        <p className="mt-2 text-sm text-gray-700">
          Imports one <strong>level</strong> at a time from a <strong>CSV</strong> file. Rows are
          grouped by <strong>Week</strong>; each week becomes one plan with a list of daily
          sessions. Only <code>Week</code> and <code>Day</code> are required — other columns are
          optional, and unknown columns (like <code>Date</code>) are ignored.
        </p>

        <div className="mt-3 overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead>
              <tr className="text-gray-500">
                <th scope="col" className="py-1 pr-4">Column</th>
                <th scope="col" className="py-1">Required?</th>
              </tr>
            </thead>
            <tbody className="text-gray-700">
              {TRAINING_PLAN_CSV_HEADERS.map((h) => (
                <tr key={h} className="border-t border-gray-100">
                  <td className="py-1 pr-4 font-mono text-xs">{h}</td>
                  <td className="py-1">{h === 'Week' || h === 'Day' ? 'Required' : 'Optional'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-2 text-xs text-gray-500">
          Accepted <code>Day</code> values: Mon, Tue, Wed, Thu, Fri, Sat, Sun (long forms like
          "Monday" also work).
        </p>

        <div className="mt-4 rounded-md bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900">
          <p className="font-medium">What happens to existing plans</p>
          <ul className="mt-1 list-disc pl-5 space-y-1">
            <li>
              <strong>A week in your file replaces the whole existing week</strong> for that level.
              A day you include overwrites the existing day; a day you leave out of a week that is
              in your file is <strong>removed</strong>.
            </li>
            <li>Weeks <strong>not</strong> in your file are left unchanged.</li>
            <li>
              Two rows for the <strong>same day in the same week</strong> create <strong>two</strong>{' '}
              sessions for that day.
            </li>
          </ul>
        </div>

        <div className="mt-4 text-sm text-gray-700">
          <p className="font-medium">Getting your data in (one level at a time)</p>
          <ul className="mt-1 list-disc pl-5 space-y-1">
            <li>
              <strong>Paste from Excel / Numbers / Google Sheets (easiest)</strong> — you can
              select the whole sheet (⌘A / Ctrl+A) and paste into the box below, as long as the
              paste includes the header row. Title banners, blank rows, extra columns (like{' '}
              <code>Date</code>), and any rows that don't fit the template are ignored
              automatically — the preview shows how many rows were skipped. No CSV export needed.
            </li>
            <li>
              <strong>Or use a CSV</strong> — <em>File → Save As / Export → CSV</em>, then upload
              the file or paste its text. Use the template below if you want the exact columns.
            </li>
          </ul>
        </div>

        <div className="mt-4">
          <Button
            variant="secondary"
            size="small"
            onClick={handleTemplate}
            aria-label="Download CSV template"
            data-testid="skills-import-template-button"
          >
            Download CSV template
          </Button>
        </div>
      </section>

      {/* Input panel */}
      {phase === 'input' && (
        <form
          onSubmit={handlePreview}
          className="mb-6 space-y-4 rounded-xl border border-gray-200 bg-white p-4 sm:p-5"
          data-testid="skills-import-input-panel"
        >
          <div>
            <label htmlFor="import_level" className="block text-sm font-medium text-gray-700 mb-1">
              Level *
            </label>
            <select
              id="import_level"
              value={level}
              onChange={handleLevelChange}
              className={`${INPUT_CLASSES} max-w-xs`}
              aria-label="Import level"
              data-testid="skills-import-level-select"
            >
              <option value="" disabled>Choose a level…</option>
              {TRAINING_PLAN_LEVELS.map((l) => (
                <option key={l} value={l}>{l}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">CSV file</label>
            <input
              type="file"
              accept=".csv,.tsv,.txt,text/csv,text/tab-separated-values,text/plain"
              onChange={handleFile}
              className="block w-full text-sm text-gray-600 file:mr-3 file:rounded-md file:border-0 file:bg-primary-50 file:px-3 file:py-2 file:text-sm file:font-medium file:text-primary-700 hover:file:bg-primary-100"
              aria-label="CSV file"
              data-testid="skills-import-file-input"
            />
            {fileName && <p className="mt-1 text-xs text-gray-500">Loaded: {fileName}</p>}
          </div>

          <div>
            <label htmlFor="csv_text" className="block text-sm font-medium text-gray-700 mb-1">
              …or paste from a spreadsheet (Excel/Numbers) or CSV
            </label>
            <textarea
              id="csv_text"
              rows={5}
              value={csv}
              onChange={handleCsvChange}
              className={`${INPUT_CLASSES} font-mono text-xs`}
              placeholder="Paste rows copied from Excel/Numbers (including the header row), or CSV text…"
              aria-label="Spreadsheet or CSV text"
              data-testid="skills-import-csv-textarea"
            />
          </div>

          <Button
            type="submit"
            variant="primary"
            disabled={busy || !level}
            aria-label="Preview import"
            data-testid="skills-import-preview-button"
          >
            {busy ? 'Checking…' : 'Preview import'}
          </Button>
        </form>
      )}

      {/* Preview panel */}
      {phase === 'preview' && (
        <section
          className="mb-6 rounded-xl border border-gray-200 bg-white p-4 sm:p-5"
          aria-live="polite"
          data-testid="skills-import-preview-panel"
        >
          {hasBlockingErrors ? (
            <div>
              <h2 className="text-lg font-semibold text-red-700">We found problems in your file</h2>
              <p className="mt-1 text-sm text-gray-700">
                Nothing has been imported. Fix these rows and preview again.
              </p>
              <ul
                className="mt-3 list-disc space-y-1 pl-5 text-sm text-red-700"
                data-testid="skills-import-errors"
              >
                {validationErrors.map((err, i) => (
                  <li key={i}>{err}</li>
                ))}
              </ul>
              <div className="mt-4">
                <Button variant="secondary" onClick={resetToInput} aria-label="Edit and try again">
                  Edit &amp; try again
                </Button>
              </div>
            </div>
          ) : preview ? (
            <div>
              <h2 className="text-lg font-semibold text-primary-900">
                Preview — {preview.level}
              </h2>
              <p className="mt-1 text-sm text-gray-700" data-testid="skills-import-totals">
                {preview.totals.weeks} week{preview.totals.weeks !== 1 ? 's' : ''} ·{' '}
                {preview.totals.sessions} session{preview.totals.sessions !== 1 ? 's' : ''} ·{' '}
                <span className="text-green-700">{preview.totals.weeks_new} new</span>,{' '}
                <span className="text-amber-700">{preview.totals.weeks_replaced} will be replaced</span>
                {preview.totals.rows_ignored > 0 && (
                  <>
                    {' '}·{' '}
                    <span className="text-gray-500">
                      {preview.totals.rows_ignored} row{preview.totals.rows_ignored !== 1 ? 's' : ''} ignored
                    </span>
                  </>
                )}
              </p>

              <div className="mt-3 overflow-x-auto">
                <table className="min-w-full text-left text-sm">
                  <thead>
                    <tr className="text-gray-500">
                      <th scope="col" className="py-1 pr-4">Week</th>
                      <th scope="col" className="py-1 pr-4">Training Phase</th>
                      <th scope="col" className="py-1 pr-4">Skill Emphasis</th>
                      <th scope="col" className="py-1 pr-4">Sessions</th>
                      <th scope="col" className="py-1">Action</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-700">
                    {preview.weeks.map((w) => (
                      <tr key={w.week} className="border-t border-gray-100">
                        <td className="py-1 pr-4 font-semibold">Week {w.week}</td>
                        <td className="py-1 pr-4">{w.training_phase || '—'}</td>
                        <td className="py-1 pr-4">{w.skill_emphasis || '—'}</td>
                        <td className="py-1 pr-4">{w.session_count}</td>
                        <td className="py-1">
                          <span
                            className={`rounded px-1.5 py-0.5 text-xs font-medium ${
                              w.action === 'replace'
                                ? 'bg-amber-100 text-amber-800'
                                : 'bg-green-100 text-green-800'
                            }`}
                          >
                            {w.action === 'replace' ? 'Replace' : 'Create'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {preview.totals.weeks_replaced > 0 && (
                <p className="mt-3 text-sm text-amber-800">
                  {preview.totals.weeks_replaced} existing week
                  {preview.totals.weeks_replaced !== 1 ? 's' : ''} will be fully replaced.
                </p>
              )}

              <div className="mt-4 flex gap-3">
                <Button
                  variant="primary"
                  onClick={handleConfirm}
                  disabled={busy}
                  aria-label="Confirm import"
                  data-testid="skills-import-confirm-button"
                >
                  {busy ? 'Importing…' : 'Confirm import'}
                </Button>
                <Button
                  variant="secondary"
                  onClick={resetToInput}
                  disabled={busy}
                  aria-label="Cancel import"
                  data-testid="skills-import-cancel-button"
                >
                  Cancel / Edit
                </Button>
              </div>
            </div>
          ) : null}
        </section>
      )}

      {/* Done panel */}
      {phase === 'done' && result && (
        <section
          className="mb-6 rounded-xl border border-green-200 bg-green-50 p-4 sm:p-5"
          aria-live="polite"
          data-testid="skills-import-done-panel"
        >
          <h2 className="text-lg font-semibold text-green-800">Import complete</h2>
          <p className="mt-1 text-sm text-gray-700">
            Imported {result.weeks_imported} week{result.weeks_imported !== 1 ? 's' : ''} (
            {result.sessions_imported} session{result.sessions_imported !== 1 ? 's' : ''}) for{' '}
            {result.level}
            {result.weeks_replaced > 0
              ? `, replacing ${result.weeks_replaced} existing week${result.weeks_replaced !== 1 ? 's' : ''}`
              : ''}
            {result.rows_ignored > 0
              ? `. ${result.rows_ignored} row${result.rows_ignored !== 1 ? 's' : ''} that didn't fit the template ${result.rows_ignored !== 1 ? 'were' : 'was'} ignored`
              : ''}
            .
          </p>
          <div className="mt-4 flex gap-3">
            <Button variant="secondary" onClick={startOver} aria-label="Import another">
              Import another
            </Button>
            <Button variant="primary" onClick={() => navigate('/skills')} aria-label="Back to Training Plans">
              Back to Training Plans
            </Button>
          </div>
        </section>
      )}
    </div>
  );
}
